import React, { useState } from 'react';
import { useShuttleBookings } from '@/hooks/useShuttleBookings';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Search, Trash2, Calendar, MapPin, Users, CreditCard, Phone, Mail, CheckCircle, Clock, XCircle, Package, List, CalendarDays } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import ShuttleBookingsCalendar from '@/components/admin/ShuttleBookingsCalendar';

const AdminShuttleBookings = () => {
  const { bookings, loading, error, updateBookingStatus, deleteBooking } = useShuttleBookings();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch =
      booking.booking_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.pickup_location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.dropoff_location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || booking.booking_status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    try {
      await updateBookingStatus(id, newStatus);
      toast({ title: "Status Updated", description: `Booking status updated to ${newStatus}.` });
    } catch {
      toast({ title: "Error", description: "Failed to update status.", variant: "destructive" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteBooking(id);
      toast({ title: "Deleted", description: "Booking deleted successfully." });
    } catch {
      toast({ title: "Error", description: "Failed to delete booking.", variant: "destructive" });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed': return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Confirmed</Badge>;
      case 'cancelled': return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default: return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading shuttle bookings...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Shuttle Bookings</h1>
          <p className="text-muted-foreground">Manage all shuttle/transfer booking requests</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Total', count: bookings.length, color: 'text-primary' },
            { label: 'Pending', count: bookings.filter(b => b.booking_status === 'pending').length, color: 'text-yellow-600' },
            { label: 'Confirmed', count: bookings.filter(b => b.booking_status === 'confirmed').length, color: 'text-green-600' },
            { label: 'Cancelled', count: bookings.filter(b => b.booking_status === 'cancelled').length, color: 'text-red-600' },
          ].map(stat => (
            <Card key={stat.label}>
              <CardContent className="p-4 text-center">
                <div className={`text-2xl font-bold ${stat.color}`}>{stat.count}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabs: Table / Calendar */}
        <Tabs defaultValue="table">
          <TabsList>
            <TabsTrigger value="table" className="gap-1.5"><List className="w-4 h-4" />Table</TabsTrigger>
            <TabsTrigger value="calendar" className="gap-1.5"><CalendarDays className="w-4 h-4" />Calendar</TabsTrigger>
          </TabsList>

          <TabsContent value="table" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input placeholder="Search by code, name, email, or route..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Bookings List */}
            <div className="space-y-4">
              {filteredBookings.length === 0 ? (
                <Card><CardContent className="p-8 text-center"><p className="text-muted-foreground">No bookings found.</p></CardContent></Card>
              ) : (
                filteredBookings.map((booking) => (
                  <Card key={booking.id} className="overflow-hidden">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{booking.booking_code}</CardTitle>
                          <p className="text-sm text-muted-foreground">Booked on {formatDate(booking.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(booking.booking_status)}
                          <Badge variant="outline">{booking.payment_method}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="space-y-1">
                          <h4 className="font-semibold text-sm text-muted-foreground">Customer</h4>
                          <p className="font-medium">{booking.full_name}</p>
                          <div className="flex items-center text-sm text-muted-foreground"><Mail className="w-3 h-3 mr-1" />{booking.email}</div>
                          <div className="flex items-center text-sm text-muted-foreground"><Phone className="w-3 h-3 mr-1" />{booking.phone}</div>
                        </div>
                        <div className="space-y-1">
                          <h4 className="font-semibold text-sm text-muted-foreground">Trip</h4>
                          <div className="flex items-center text-sm"><MapPin className="w-3 h-3 mr-1 text-muted-foreground" />{booking.pickup_location} → {booking.dropoff_location}</div>
                          <div className="flex items-center text-sm text-muted-foreground"><Users className="w-3 h-3 mr-1" />{booking.passengers} pax</div>
                          <div className="text-sm text-muted-foreground capitalize">{booking.trip_type} • {booking.route_type}</div>
                          {booking.vehicle_name && <div className="text-sm text-muted-foreground">Vehicle: {booking.vehicle_name}</div>}
                        </div>
                        <div className="space-y-1">
                          <h4 className="font-semibold text-sm text-muted-foreground">Payment</h4>
                          <div className="flex items-center text-sm"><CreditCard className="w-3 h-3 mr-1 text-muted-foreground" /><span className="font-semibold">${booking.total_price.toFixed(2)}</span></div>
                          <p className="text-sm text-muted-foreground capitalize">Status: {booking.payment_status}</p>
                          {booking.promo_code && <p className="text-xs text-muted-foreground">Promo: {booking.promo_code} (-${booking.discount.toFixed(2)})</p>}
                        </div>
                      </div>

                      {(booking.arrival_date || booking.airline) && (
                        <>
                          <Separator className="my-4" />
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="font-semibold text-sm text-muted-foreground mb-1"><Calendar className="w-3 h-3 mr-1 inline" />Arrival</h4>
                              <div className="text-sm space-y-1">
                                {booking.arrival_date && <p>Date: {booking.arrival_date}</p>}
                                {booking.arrival_time && <p>Time: {booking.arrival_time}</p>}
                                {booking.airline && <p>Airline: {booking.airline}</p>}
                                {booking.flight_number && <p>Flight: {booking.flight_number}</p>}
                              </div>
                            </div>
                            {booking.trip_type === 'roundtrip' && booking.return_date && (
                              <div>
                                <h4 className="font-semibold text-sm text-muted-foreground mb-1"><Calendar className="w-3 h-3 mr-1 inline" />Return</h4>
                                <div className="text-sm space-y-1">
                                  {booking.return_date && <p>Date: {booking.return_date}</p>}
                                  {booking.return_time && <p>Time: {booking.return_time}</p>}
                                  {booking.return_airline && <p>Airline: {booking.return_airline}</p>}
                                  {booking.return_flight_number && <p>Flight: {booking.return_flight_number}</p>}
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}

                      {booking.extra_items && booking.extra_items.length > 0 && (
                        <>
                          <Separator className="my-4" />
                          <div>
                            <h4 className="font-semibold text-sm text-muted-foreground mb-2"><Package className="w-3 h-3 mr-1 inline" />Extras (${booking.extras_cost.toFixed(2)})</h4>
                            {booking.extra_items.map((item: any, i: number) => (
                              <div key={i} className="text-sm flex justify-between">
                                <span>{item.name} x{item.quantity}</span>
                                <span>${(item.price * item.quantity).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      <Separator className="my-4" />
                      <div className="flex justify-between items-center">
                        <Select value={booking.booking_status} onValueChange={(v) => handleStatusUpdate(booking.id, v)}>
                          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm"><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Booking</AlertDialogTitle>
                              <AlertDialogDescription>Delete booking {booking.booking_code}? This cannot be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(booking.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="calendar">
            <Card>
              <CardContent className="p-6">
                <ShuttleBookingsCalendar bookings={bookings} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminShuttleBookings;
